import styled from '@emotion/styled'
import { ApiKey } from '@pubstack/common/src/site'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useApiKeys, useCreateApiKeys } from '~/api/app/api-keys.api'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import Table, { Column, EmptyTable, handleTableSearchAndSort, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { ADMIN_SCOPE_DETAIL_HEADER_HEIGHT_PX } from '~/modules/admin/PureAdminScopeDetail'

const ScopeApiKeysWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 10px;
`

const ApiKeyTableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const NewKeyModal: FunctionComponent = () => {
  const modal = useGlobalModal()
  const { control, reset, handleSubmit } = useForm<Pick<ApiKey, 'name'>>({
    defaultValues: {
      name: '',
    },
  })
  const name = useWatch({ control, name: 'name' })
  const { mutateAsync: createKey } = useCreateApiKeys({
    errorToast: 'Error on creating API Key',
    successToast: (
      <>
        <strong>{name}</strong> was successfully created
      </>
    ),
  })

  const close = () => {
    reset()
    modal.close()
  }

  const onSubmit = async (key: Pick<ApiKey, 'name'>) => {
    if (key.name) {
      await createKey({ name: key.name })
    }
    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Add API key</Modal.Title>

      <Modal.Body>
        <Input name={'name'} rules={{ required: true }} labelIsPlaceholder control={control} label={'API key name'} />
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-admin-scopes-detail-api-keys-creation-cancel'}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)} trackingLabel={'common-admin-scopes-detail-api-keys-creation-confirm'}>
          Create key
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

const ADMIN_KEYS_COLUMNS: (Column | SortableColumn<ApiKey>)[] = [
  {
    name: 'Name',
    isSortable: true,
    order: 'ascending',
    attributeSort: 'name',
  },
  {
    name: 'Key',
    isSortable: true,
    order: 'none',
    attributeSort: 'id',
  },
]

export const SettingsApiKeys: FunctionComponent = () => {
  const { data: apiKeys, isFetching } = useApiKeys()

  const { control } = useForm<Pick<ApiKey, 'name'>>({
    defaultValues: {
      name: '',
    },
  })
  const modal = useGlobalModal()
  const keySearch = useWatch({ control, name: 'name' })

  const [columns, setColumns] = useState<TableColumns<ApiKey>>(ADMIN_KEYS_COLUMNS)

  const displayedKeys = handleTableSearchAndSort(columns, apiKeys || [], keySearch, ['name'])

  const createNewApiKeyButton = (
    <Button onClick={() => modal.open(NewKeyModal, {})} trackingLabel={'common-admin-scopes-detail-api-keys-new'}>
      New API key
    </Button>
  )
  return (
    <ScopeApiKeysWrapper>
      <ApiKeyTableActions>
        <Input iconLeft={'search'} name={'name'} control={control} label={'Search'} labelIsPlaceholder />
        {createNewApiKeyButton}
      </ApiKeyTableActions>
      <Table
        columns={columns}
        headersTop={ADMIN_SCOPE_DETAIL_HEADER_HEIGHT_PX}
        isLoading={isFetching}
        onClickHeading={(column) => {
          onColumnSort(columns, column, setColumns)
        }}
      >
        {displayedKeys.length ? (
          displayedKeys.map((apiKey, index) => (
            <TableRow key={index}>
              <TableCell>{apiKey.name}</TableCell>
              <TableCell>{apiKey.id}</TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colspan={columns.length}>
              <EmptyTable>
                <div>
                  <h2>No API key</h2>
                  <p>You do not have any API keys yet.</p>
                </div>
                {createNewApiKeyButton}
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </ScopeApiKeysWrapper>
  )
}
